import { AppointmentsTable } from '@/components/appointments-table';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Spinner } from '@/components/ui/spinner';
import { useAuth } from '@/providers/auth-provider';
import { getInstallerAppointmentsFiltered } from '@/lib/getInstallerAppointments';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const AppointmentList = () => {
  const { session } = useAuth();
  const { t } = useTranslation();

  const [selectedFilter, setSelectedFilter] = useState(() => {
    return localStorage.getItem('selectedFilter') || 'This Week';
  });
  const [selectedStatus, setSelectedStatus] = useState(() => {
    return localStorage.getItem('selectedStatus') || 'All';
  });

  useEffect(() => {
    localStorage.setItem('selectedFilter', selectedFilter);
  }, [selectedFilter]);

  useEffect(() => {
    localStorage.setItem('selectedStatus', selectedStatus);
  }, [selectedStatus]);

  const { data: appointments, isLoading } = useQuery({
    networkMode: 'offlineFirst',
    queryKey: ['appointments', session?.user.id, selectedFilter, selectedStatus],
    queryFn: ({ queryKey }) =>
      getInstallerAppointmentsFiltered(queryKey[1]!, queryKey[2]!, queryKey[3]!), // queryKey[1] is the user id and queryKey[2] is the selected value
  });

  const handleFilterClick = (value: string) => {
    setSelectedFilter(value);
  };

  const handleStatusClick = (value: string) => {
    setSelectedStatus(value);
  };

  return (
    <div className="text-card-foreground">
      <div className="flex flex-col space-y-4 md:flex-row md:items-center md:justify-between md:space-y-0 pb-7">
        <h1 className="text-xl md:text-2xl font-bold">{t('appointments')}</h1>
        <div className="inline-flex gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                className="w-full md:w-auto text-foreground capitalize"
              >
                {t(selectedFilter.toLowerCase().replace(/\s/g, ''))}
                <ChevronDown className="ml-2 h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-full md:w-auto">
              <DropdownMenuItem onClick={() => handleFilterClick('All')}>
                {t('alltime')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleFilterClick('Today')}>
                {t('today')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleFilterClick('Tomorrow')}>
                {t('tomorrow')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleFilterClick('This Week')}>
                {t('thisweek')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleFilterClick('This Month')}>
                {t('thismonth')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="w-full md:w-auto capitalize">
                {t(selectedStatus.toLowerCase().replace(/\s/g, ''))}
                <ChevronDown className="ml-2 h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-full md:w-auto">
              <DropdownMenuItem onClick={() => handleStatusClick('All')}>
                {t('all')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleStatusClick('Scheduled')}>
                {t('scheduled')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleStatusClick('Completed')}>
                {t('completed')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      <div>
        {appointments && appointments.length === 0 ? (
          <div className="text-center text-muted-foreground">{t('noappointments')}</div>
        ) : (
          <AppointmentsTable data={appointments || []} />
        )}

        {isLoading && <Spinner />}
      </div>
    </div>
  );
};

export default AppointmentList;
